import type { BuildingType, MonsterType } from "../domain/types.js";
import type {
  CampaignMapVisualConfig,
  CampaignMonsterVisualConfig,
  CampaignTerrainTileBases,
  CampaignVisualConfig,
} from "./types.js";

export interface CampaignVisualAssetFiles {
  mapBackground: string;
  storyIntro: string;
  buildings: Record<BuildingType, string>;
  pathEndpoints: {
    startStrip: string;
    startFallback: string;
    end: string;
  };
  monsters: Record<MonsterType, {
    sheet: string;
    fallback: string;
  }>;
}

export const commonVisualAssetFiles = {
  mapBackground: "map-grid.png",
  storyIntro: "story_intro_1.png",
  buildings: {
    Tower: "tower.png",
    Wall: "wall.png",
    Amplifier: "amplifier.png",
    Trap: "trap.png",
    Shrine: "shrine.png",
  },
  pathEndpoints: {
    startStrip: "spawn-portal-strip.png",
    startFallback: "spawn-portal.png",
    end: "exit-core.png",
  },
  monsters: {
    Normal: { sheet: "normal-sheet.png", fallback: "normal.png" },
    Swarm: { sheet: "swarm-sheet.png", fallback: "swarm.png" },
    Armored: { sheet: "armored-sheet.png", fallback: "armored.png" },
    Giant: { sheet: "giant-sheet.png", fallback: "giant.png" },
    Runner: { sheet: "normal-sheet.png", fallback: "normal.png" },
  },
} as const satisfies CampaignVisualAssetFiles;

export function withBase(path: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return base + path;
}

export const commonTerrainTileBases = {
  path: withBase("/common/terrain/path"),
  buildable: withBase("/common/terrain/buildable"),
  forest: withBase("/common/terrain/forest"),
  rocks: withBase("/common/terrain/rocks"),
} as const satisfies CampaignTerrainTileBases;

export function defineCampaignVisualAssetFiles<T extends CampaignVisualAssetFiles>(files: T): T {
  return files;
}

export function defineCampaignTerrainTileBases(
  tileBases: Partial<CampaignTerrainTileBases> = {}
): CampaignTerrainTileBases {
  return {
    ...commonTerrainTileBases,
    ...tileBases,
  };
}

export function collectCampaignVisualAssetFiles(files: CampaignVisualAssetFiles): string[] {
  return [
    files.mapBackground,
    files.storyIntro,
    ...Object.values(files.buildings),
    files.pathEndpoints.startStrip,
    files.pathEndpoints.startFallback,
    files.pathEndpoints.end,
    ...Object.values(files.monsters).flatMap((monster) => [monster.sheet, monster.fallback]),
  ];
}

export function createCampaignLocalAssetSet(
  files: CampaignVisualAssetFiles,
  options: {
    extraFiles?: readonly string[];
    excludeFiles?: readonly string[];
  } = {}
): ReadonlySet<string> {
  const excluded = new Set(options.excludeFiles ?? []);
  return new Set([
    ...collectCampaignVisualAssetFiles(files),
    ...(options.extraFiles ?? []),
  ].filter((fileName) => !excluded.has(fileName)));
}

export function commonAsset(fileName: string): string {
  return withBase(`/common/${fileName}`);
}

export function campaignAsset(campaignId: string, fileName: string): string {
  return withBase(`/campaigns/${campaignId}/${fileName}`);
}

export function createCampaignAssetResolver(
  campaignId: string,
  campaignLocalFiles: ReadonlySet<string>
): (fileName: string) => string {
  return (fileName: string) =>
    campaignLocalFiles.has(fileName) ? campaignAsset(campaignId, fileName) : commonAsset(fileName);
}

export interface CampaignVisualDecorationAsset {
  fileName: string;
  x: number;
  y: number;
  scale: number;
}

type MonsterVisualTuning = Partial<
  Pick<CampaignMonsterVisualConfig, "sheetFrameSize" | "sheetFrames" | "tint" | "scale">
>;

const DEFAULT_MONSTER_VISUAL_TUNING: Record<MonsterType, Required<Pick<CampaignMonsterVisualConfig, "sheetFrameSize" | "sheetFrames" | "scale">>> = {
  Normal: { sheetFrameSize: 128, sheetFrames: 2, scale: 1 },
  Swarm: { sheetFrameSize: 128, sheetFrames: 2, scale: 0.75 },
  Armored: { sheetFrameSize: 128, sheetFrames: 2, scale: 1.15 },
  Giant: { sheetFrameSize: 128, sheetFrames: 2, scale: 1.55 },
  Runner: { sheetFrameSize: 128, sheetFrames: 2, scale: 0.9 },
};

const DEFAULT_MONSTER_TINTS: Partial<Record<MonsterType, string>> = {
  Giant: "rgba(255, 80, 32, 0.35)",
  Runner: "rgba(80, 180, 255, 0.25)",
};

export interface CampaignVisualConfigFromManifestOptions {
  campaignId: string;
  files: CampaignVisualAssetFiles;
  campaignLocalFiles: ReadonlySet<string>;
  mapDecorations?: readonly CampaignVisualDecorationAsset[];
  storyImageFiles?: readonly string[];
  monsterVisualTuning?: Partial<Record<MonsterType, MonsterVisualTuning>>;
  terrainTileBases?: Partial<CampaignTerrainTileBases>;
  fieldVisuals?: CampaignVisualConfig["fieldVisuals"];
}

function buildMonsterVisual(
  type: MonsterType,
  files: CampaignVisualAssetFiles,
  asset: (fileName: string) => string,
  tuning: MonsterVisualTuning = {}
): CampaignMonsterVisualConfig {
  const defaults = DEFAULT_MONSTER_VISUAL_TUNING[type];
  const tint = tuning.tint ?? DEFAULT_MONSTER_TINTS[type];
  return {
    sheetSrc: asset(files.monsters[type].sheet),
    sheetFrameSize: tuning.sheetFrameSize ?? defaults.sheetFrameSize,
    sheetFrames: tuning.sheetFrames ?? defaults.sheetFrames,
    fallbackSrc: asset(files.monsters[type].fallback),
    ...(tint ? { tint } : {}),
    scale: tuning.scale ?? defaults.scale,
  };
}

export function createCampaignVisualConfigFromManifest({
  campaignId,
  files,
  campaignLocalFiles,
  mapDecorations = [],
  storyImageFiles = [files.storyIntro],
  monsterVisualTuning = {},
  terrainTileBases = {},
  fieldVisuals,
}: CampaignVisualConfigFromManifestOptions): CampaignVisualConfig {
  const asset = createCampaignAssetResolver(campaignId, campaignLocalFiles);
  const monsterTypes: MonsterType[] = ["Normal", "Swarm", "Armored", "Giant", "Runner"];
  return {
    map: {
      backgroundSrc: asset(files.mapBackground),
      decorations: mapDecorations.map((decoration): CampaignMapVisualConfig["decorations"][number] => ({
        imageSrc: asset(decoration.fileName),
        x: decoration.x,
        y: decoration.y,
        scale: decoration.scale,
      })),
    },
    buildings: {
      Tower: { spriteSrc: asset(files.buildings.Tower) },
      Wall: { spriteSrc: asset(files.buildings.Wall) },
      Amplifier: { spriteSrc: asset(files.buildings.Amplifier) },
      Trap: { spriteSrc: asset(files.buildings.Trap) },
      Shrine: { spriteSrc: asset(files.buildings.Shrine) },
    },
    pathEndpoints: {
      startStripSrc: asset(files.pathEndpoints.startStrip),
      startFallbackSrc: asset(files.pathEndpoints.startFallback),
      endSrc: asset(files.pathEndpoints.end),
    },
    monsters: Object.fromEntries(
      monsterTypes.map((type) => [
        type,
        buildMonsterVisual(type, files, asset, monsterVisualTuning[type]),
      ])
    ) as Record<MonsterType, CampaignMonsterVisualConfig>,
    terrain: {
      tileBases: defineCampaignTerrainTileBases(terrainTileBases),
    },
    story: {
      imageAliases: Object.fromEntries(
        storyImageFiles.map((fileName) => {
          return [fileName, asset(fileName)];
        })
      ),
    },
    ...(fieldVisuals ? { fieldVisuals } : {}),
  };
}
